/** An error carrying the HTTP status and server-provided message. */
export class ApiError extends Error {
  constructor(
    public status: number,
    message: string,
    public details?: unknown,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

type Method = 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';

/** Wordt aangeroepen zodra de server laat weten dat de sessie er niet meer is.
 *  De AuthProvider hangt zich hieraan op en zet de gebruiker op `null`, waarna
 *  ProtectedRoute de redirect naar /login doet — zo hoeft deze module niets van
 *  de router of van React te weten. */
let onSessionLost: (() => void) | null = null;

export function setSessionLostHandler(fn: (() => void) | null) {
  onSessionLost = fn;
}

/** Een 401 op de auth-routes zelf hoort bij het inloggen (verkeerd wachtwoord)
 *  en betekent juist niet dat een bestaande sessie is weggevallen. Die moet op
 *  het formulier belanden, niet in een uitlog. */
const isAuthRoute = (path: string) => path.startsWith('/auth/');

async function request<T>(method: Method, path: string, body?: unknown): Promise<T> {
  const opts: RequestInit = { method, credentials: 'include', headers: {} };
  if (body !== undefined) {
    if (body instanceof FormData) {
      opts.body = body;
    } else {
      opts.headers = { 'Content-Type': 'application/json' };
      opts.body = JSON.stringify(body);
    }
  }
  const res = await fetch(`/api${path}`, opts);
  const text = await res.text();
  const data = text ? (JSON.parse(text) as unknown) : null;
  if (!res.ok) {
    if (res.status === 401 && !isAuthRoute(path)) onSessionLost?.();
    const err = data as { error?: string; details?: unknown } | null;
    throw new ApiError(res.status, err?.error ?? res.statusText, err?.details);
  }
  return data as T;
}

export const api = {
  get: <T>(path: string) => request<T>('GET', path),
  post: <T>(path: string, body?: unknown) => request<T>('POST', path, body),
  patch: <T>(path: string, body?: unknown) => request<T>('PATCH', path, body),
  del: <T>(path: string) => request<T>('DELETE', path),
};
